/*
  TODOs:

  * Make `useAsync` hook (or use react-query)
    - So we don't have to handle setting data, statuses, and errors
*/

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { Spinner } from '@components/Spinner';
import { ErrorFallback } from '@components/ErrorFallback';
import * as OAuth2 from './OAuth2';
import {
  AuthProviderProps,
  Status,
  STATUS,
  UserData,
  AuthContext as AuthContextType,
} from './types';
import { defaultUserData, hasAuthParams } from './utils';
import { client } from '../../utils/client';
import jwtDecode from 'jwt-decode';
import { useQuery } from 'react-query';
import { clearSentryUser } from '../../utils/sentry';
import { useQueryParams } from '../../hooks/useQueryParams/index';
import { APP_DEFAULT_MODE } from '../../constants/env';

const AuthContext = createContext<AuthContextType>({
  user: null,
  merchantId: null,
  testMode: false,
  changePassword: () => {},
  logout: () => {},
  loginWithRedirect: () => {},
});
AuthContext.displayName = 'AuthContext';

function AuthProvider(props: AuthProviderProps) {
  const query = useQueryParams();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const [user, setUser] = useState<any>(null);
  const [status, setStatus] = useState<Status>(STATUS.pending);
  const [error, setError] = useState<any>(null);
  const [testMode, setTestMode] = useState<boolean>(
    (!sessionStorage.getItem('testmode') && APP_DEFAULT_MODE === 'test') ||
      query.get('testmode') === 'true' ||
      sessionStorage.getItem('testmode') === 'true'
  );
  const oAuth2Client = useMemo(() => OAuth2.oAuth2Client(testMode), [testMode]);

  useEffect(() => {
    sessionStorage.setItem('testmode', testMode ? 'true' : 'false');
  }, [testMode]);

  useEffect(() => {
    if (query.get('testmode') === 'true') {
      setTestMode(true);
    } else if (query.get('testmode') === 'false') {
      setTestMode(false);
    }
  }, [query]);

  useEffect(() => {
    (async () => {
      try {
        if (hasAuthParams()) {
          await oAuth2Client.handleRedirectCallback().then(data => {
            navigate(sessionStorage.getItem('redirect_to') || '/');
            setUser(data);
          });
        } else if (oAuth2Client.hasRefreshToken()) {
          await oAuth2Client.refreshToken().then(data => {
            setUser(data);
          });
        } else {
          setStatus(STATUS.idle);
        }
      } catch (error) {
        setError(error);
        setStatus(STATUS.error);
      }
    })();
  }, [navigate, setTestMode, oAuth2Client]);

  useEffect(() => {
    if (user) {
      setStatus(STATUS.success);
    }
  }, [user]);

  const merchantId = useMemo(() => {
    if (user && user.accessToken) {
      const decodedJwt: any = jwtDecode(user.accessToken);

      return decodedJwt?.ext?.roles[0]?.resource_id ?? null;
    }
  }, [user]);

  const loginWithRedirect = useCallback(async () => {
    const uri = await oAuth2Client.getAuthorizationUri();
    sessionStorage.setItem('redirect_to', `${pathname}${hash}`);
    window.location.href = uri;
  }, [pathname, hash, oAuth2Client]);

  const logout = useCallback(async () => {
    const uri = await oAuth2Client.buildLogoutUrl(user?.access_token);
    // TODO: Maybe dump all of this inside the logout button function instead
    sessionStorage.clear();
    localStorage.clear();
    clearSentryUser();
    window.location.href = uri;
  }, [user, oAuth2Client]);

  const changePassword = useCallback(() => {
    const uri = oAuth2Client.buildChangePasswordUrl();
    window.location.href = uri;
  }, [oAuth2Client]);

  const value = useMemo(
    () => ({
      user,
      merchantId,
      testMode,
      changePassword,
      logout,
      loginWithRedirect,
    }),
    [user, merchantId, testMode, changePassword, logout, loginWithRedirect]
  );

  if (status === STATUS.idle) {
    loginWithRedirect();

    return <Spinner />;
  }

  if (status === STATUS.pending) {
    return <Spinner />;
  }

  if (status === STATUS.error) {
    const errorObj = {
      message: error,
    };

    return <ErrorFallback error={errorObj} recovery={logout} />;
  }

  return <AuthContext.Provider value={value} {...props} />;
}

function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

function useAuthorizedClient() {
  const {
    user: { accessToken: token },
  }: any = useAuth();
  return useCallback(
    (endpoint, config) => client(endpoint, { ...config, token }),
    [token]
  );
}

function useUserData() {
  const {
    user: { accessToken: token },
  }: any = useAuth();
  const { sub }: { sub: string } = jwtDecode(token);
  const client = useAuthorizedClient();
  const [userData, setUserData] = useState<UserData>(defaultUserData);

  const { data, status } = useQuery(['userData', { sub }], () =>
    client(`/v1/identity/user/${sub}`, {}).then((data: UserData) => data)
  );

  useEffect(() => {
    if (status === 'success') {
      setUserData(data as UserData);
    }
  }, [data, status]);

  return userData;
}

export { useAuth, useUserData, AuthProvider, AuthContext, useAuthorizedClient };
